import { useEffect, useMemo, useRef } from 'react';

import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import {
    OrderFrequency,
    TotemState,
    UserState,
    useCreateOrderForCustomerMutation,
    useGetOrderCreationConstraintsQuery,
    TotadminPermission,
} from 'data/__generated__';
import { organizationSource, totemSource, userSource } from 'data/entity-sources/sources';
import { useScopedEntitySource } from 'data/entity-sources/useEntityOptions';

import { PAGES } from 'constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { SectionColumn, SectionContainer, ColumnsSectionContainer } from 'components/DetailsView/Section';
import { DetailFormCheckbox, DetailFormDatePicker, DetailFormSelect } from 'components/DetailsView';
import { UltraSelectField } from 'components/UltraSelect';

import { dateFromString, formatDateAsAnniversary, formatDateForDisplay } from 'helpers/dateTimes';
import { getFrequencyLabel } from 'pages/Orders/constants';
import {
    DEFAULT_DELIVERY_TIME_WINDOW,
    DELIVERY_WINDOW_START_OPTIONS,
    DELIVERY_WINDOW_STOP_OPTIONS,
    getDeliveryWindowError,
    isDeliveryWindowCharged,
    MIN_FREE_INTERVAL_HOURS,
} from 'pages/Orders/constants/deliveryTimeWindow';

type FormData = {
    organizationId: string | null;
    userId: string | null;
    totemId: string | null;
    isPunctual: boolean;
    frequency: OrderFrequency;
    deliveryDate: string | null;
    deliveryTimeWindowStart: string;
    deliveryTimeWindowStop: string;
};

const FREQUENCY_OPTIONS = Object.values(OrderFrequency).map((frequency) => ({
    value: frequency,
    label: getFrequencyLabel(frequency),
}));

/**
 * Création d'une commande VIDE au nom d'un client, puis redirection vers sa fiche : le panier se
 * saisit ensuite depuis la section « Détails des produits ».
 *
 * Les règles client (date livrable, deadline de validation) sont affichées en avertissement, pas en
 * blocage — c'est la validation de la commande qui les appliquera pour de bon.
 */
export const OrderCreate = () => {
    const navigate = useNavigate();

    const { data: constraintsData } = useGetOrderCreationConstraintsQuery();
    const [createOrderForCustomer, { loading: createLoading }] = useCreateOrderForCustomerMutation();

    const firstDeliverableDate = constraintsData?.orderCreationConstraints.firstDeliverableDate ?? null;
    const closedDays = useMemo(
        () => (constraintsData?.orderCreationConstraints.closedDays ?? []).map(dateFromString),
        [constraintsData],
    );

    const methods = useForm<FormData>({
        defaultValues: {
            organizationId: null,
            userId: null,
            totemId: null,
            isPunctual: true,
            frequency: OrderFrequency.Weekly,
            deliveryDate: null,
            deliveryTimeWindowStart: DEFAULT_DELIVERY_TIME_WINDOW.start,
            deliveryTimeWindowStop: DEFAULT_DELIVERY_TIME_WINDOW.stop,
        },
    });

    const {
        control,
        handleSubmit,
        formState: { isDirty },
        setValue,
    } = methods;

    const organizationId = useWatch({ control, name: 'organizationId' });
    const isPunctual = useWatch({ control, name: 'isPunctual' });
    const deliveryDate = useWatch({ control, name: 'deliveryDate' });
    const windowStart = useWatch({ control, name: 'deliveryTimeWindowStart' });
    const windowStop = useWatch({ control, name: 'deliveryTimeWindowStop' });

    // Le client et le point de livraison n'ont de sens que dans l'organisation choisie.
    const scopedUserSource = useScopedEntitySource(userSource, {
        organizationIds: organizationId ? [organizationId] : [],
        permissions: null,
        states: [UserState.Regular],
    });
    const scopedTotemSource = useScopedEntitySource(totemSource, {
        organizationIds: organizationId ? [organizationId] : [],
        states: [TotemState.Rollout],
    });

    // Changer d'organisation invalide le client et le point de livraison déjà choisis : ils
    // appartiennent à l'ancienne.
    const previousOrganizationId = useRef(organizationId);
    useEffect(() => {
        if (previousOrganizationId.current === organizationId) {
            return;
        }
        previousOrganizationId.current = organizationId;
        setValue('userId', null, { shouldDirty: true });
        setValue('totemId', null, { shouldDirty: true });
    }, [organizationId, setValue]);

    const windowError = getDeliveryWindowError(windowStart, windowStop);
    const isDateTooEarly = !!deliveryDate && !!firstDeliverableDate && deliveryDate < firstDeliverableDate;

    const onSubmit = handleSubmit(async ({ userId, totemId, deliveryDate: date, frequency, ...rest }) => {
        if (!userId || !totemId || !date) {
            toast.error('Client, point de livraison et date de livraison sont obligatoires.');
            return;
        }
        if (windowError) {
            toast.error(windowError);
            return;
        }

        const { data } = await createOrderForCustomer({
            variables: {
                input: {
                    userId,
                    totemId,
                    deliveryDate: date,
                    deliveryTimeWindow: { start: rest.deliveryTimeWindowStart, stop: rest.deliveryTimeWindowStop },
                    isPunctual: rest.isPunctual,
                    frequency: rest.isPunctual ? null : frequency,
                },
            },
        });

        if (!data) {
            throw Error("Une erreur inconnue s'est produite");
        }

        const {
            createOrderForCustomer: { success, errors, warnings, order },
        } = data;

        if (!success || !order) {
            errors.forEach((error) => toast.error(error, { autoClose: false }));
            return;
        }

        warnings.forEach((warning) => toast.warning(warning, { autoClose: false }));
        toast.success('La commande a bien été créée. Ajoutez maintenant ses produits.');
        navigate(`/order/${order._id}`);
    });

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [
                {
                    label: 'Retour à la liste',
                    icon: <ArrowLeft />,
                    to: PAGES.orders.url,
                    requiredPermissions: [TotadminPermission.OrdersRead],
                },
            ],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.orderCreate} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={onSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={createLoading}
                            saveTitle="Créer"
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <SectionContainer title="Client" isInitiallyOpen>
                                <ColumnsSectionContainer numberOfColumns={2}>
                                    <SectionColumn>
                                        <UltraSelectField
                                            name="organizationId"
                                            source={organizationSource}
                                            label="Organisation"
                                            required
                                            dataTest="order-create-organization"
                                        />
                                        <UltraSelectField
                                            name="userId"
                                            source={scopedUserSource}
                                            label="Client"
                                            sublabel="Destinataire de l'email de confirmation — jamais un compte interne"
                                            required
                                            disabled={!organizationId}
                                            dataTest="order-create-user"
                                        />
                                    </SectionColumn>
                                    <SectionColumn>
                                        <UltraSelectField
                                            name="totemId"
                                            source={scopedTotemSource}
                                            label="Point de livraison"
                                            required
                                            disabled={!organizationId}
                                            dataTest="order-create-totem"
                                        />
                                    </SectionColumn>
                                </ColumnsSectionContainer>
                            </SectionContainer>
                            <SectionContainer title="Livraison" isInitiallyOpen>
                                <ColumnsSectionContainer numberOfColumns={2}>
                                    <SectionColumn>
                                        <DetailFormCheckbox
                                            label="Commande ponctuelle"
                                            sublabel="Décocher pour une commande récurrente"
                                            name="isPunctual"
                                            defaultChecked
                                        />
                                        {!isPunctual ? (
                                            <DetailFormSelect
                                                label="Fréquence"
                                                name="frequency"
                                                options={FREQUENCY_OPTIONS}
                                                defaultValue={OrderFrequency.Weekly}
                                                required
                                            />
                                        ) : null}
                                    </SectionColumn>
                                    <SectionColumn>
                                        <DetailFormDatePicker
                                            label="Date de livraison"
                                            sublabel={
                                                firstDeliverableDate
                                                    ? `Première date encore validable : ${formatDateForDisplay(firstDeliverableDate)}`
                                                    : undefined
                                            }
                                            placeholder="Date de livraison"
                                            name="deliveryDate"
                                            control={control}
                                            transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                                            transformDateToValue={(date) =>
                                                date ? formatDateAsAnniversary({ dateTime: date }) : null
                                            }
                                            excludeDates={closedDays}
                                            filterDate={isWorkedDay}
                                        />
                                        {isDateTooEarly && firstDeliverableDate ? (
                                            <Warning>
                                                Cette date est antérieure au{' '}
                                                {formatDateForDisplay(firstDeliverableDate)} : la commande sera créée,
                                                mais sa validation sera refusée tant que la date n’aura pas été
                                                repoussée.
                                            </Warning>
                                        ) : null}
                                        <DetailFormSelect
                                            label="Début du créneau"
                                            name="deliveryTimeWindowStart"
                                            options={DELIVERY_WINDOW_START_OPTIONS}
                                            defaultValue={DEFAULT_DELIVERY_TIME_WINDOW.start}
                                            required
                                        />
                                        <DetailFormSelect
                                            label="Fin du créneau"
                                            name="deliveryTimeWindowStop"
                                            options={DELIVERY_WINDOW_STOP_OPTIONS}
                                            defaultValue={DEFAULT_DELIVERY_TIME_WINDOW.stop}
                                            required
                                        />
                                        {windowError ? <Warning>{windowError}</Warning> : null}
                                        {!windowError && isDeliveryWindowCharged(windowStart, windowStop) ? (
                                            <Warning>
                                                Créneau de moins de {MIN_FREE_INTERVAL_HOURS} h : 10 € de frais de
                                                livraison supplémentaires seront appliqués à la validation.
                                            </Warning>
                                        ) : null}
                                    </SectionColumn>
                                </ColumnsSectionContainer>
                            </SectionContainer>
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

// Les livraisons ne partent pas le week-end ; les jours fériés et fermetures sont exclus à part, via
// `excludeDates`, parce qu'ils viennent du serveur.
function isWorkedDay(date: Date): boolean {
    const day = date.getDay();
    return day !== 0 && day !== 6;
}

const Container = styled.div`
    display: flex;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const Warning = styled.span`
    color: ${({ theme }) => theme.warningColor};
    font-size: 13px;
`;
